import axios from "axios";
import { orderBy, sumBy } from "lodash";
import Api from "../utils/Api";
import Resizer from "react-image-file-resizer";

export class Uploader {
  constructor(options) {
    this.chunkSize = options.chunkSize || 1024 * 1024 * 5;
    this.folder = options.folder || "";
    this.file = options.file;
    this.uploadId = [];
    this.uploadedParts = {};
    this.finishedParts = {};
    this.chunks = {};
    this.cancelTokenSource = axios.CancelToken.source();
    this.progress = {};
    this.uploadedFiles = 0;
    this.uploadIdWithFiles = [];
    this.progressParts = {};
    this.failedChunks = [];
    this.failedFinalzies = [];
    this.onProgressFn = () => {};
    this.setIsFinalized = () => {};
  }

  async resumeFaileds() {
    await Promise.all(this.failedChunks);

    this.failedFinalzies.forEach((index) => {
      this.finalize(index);
    });
  }

  round(num, rounded = 10) {
    const roundDitits = rounded || 10;

    return Math.round(num / roundDitits) * roundDitits;
  }

  async upload() {
    const allApiCals = [];
    Array.from(this.file).forEach(async (file, index) => {
      let uploadUrls = [];
      let i;
      const partItems = {};

      for (i = 1; i <= this.chunks[index]; i++) {
        partItems[i] = 0;
        const sentSize = (i - 1) * this.chunkSize;
        const body = file.slice(sentSize, sentSize + this.chunkSize);
        const url = await this.getPartsUploadUrl(body, i, file, index);
        uploadUrls[i] = { url, body };
      }

      this.progressParts[index] = partItems;

      uploadUrls.forEach(async ({ url, body }, partNumber) => {
        allApiCals.push(this.chunkUpload(url, partNumber, body, index));
      });

      uploadUrls = [];
    });

    await Promise.all(allApiCals);
  }

  async onProgressHandler(event, partNumber, index) {
    const loaded = this.round(event.loaded / 1024);
    this.progressParts[index][partNumber] = loaded;

    const totalLoaded = this.round(
      sumBy(Object.values(this.progressParts[index]))
    );

    this.progress[index].uploadedSize = totalLoaded;
    this.progress[index].percentage = this.round(
      (totalLoaded / this.progress[index].totalSize) * 100
    );

    this.onProgressFn(this.progress);
  }

  checkIfFinalized(finalize) {
    this.setIsFinalized = finalize;
    return this;
  }

  onProgress(onProgress) {
    this.onProgressFn = onProgress;
    return this;
  }

  async initializeToApi(files) {
    try {
      await Api.post("/initialize/" + this.folder, { files });

      return true;
    } catch (err) {
      return false;
    }
  }

  async init() {
    return new Promise(async (resolve, reject) => {
      let totalFileSize = Array.from(this.file).reduce((carry, item) => {
        carry += item.size;

        return carry;
      }, 0);

      totalFileSize = this.round(totalFileSize / 1024 / 1024, 10);

      if (totalFileSize >= 1000) {
        this.chunkSize = 1024 * 1024 * 50;
      } else if (totalFileSize >= 500) {
        this.chunkSize = 1024 * 1024 * 15;
      }

      const apiCalls = [];
      Array.from(this.file).forEach(async (file, index) => {
        this.progress[index] = {
          totalSize: 0,
          uploadedSize: 0,
          percentage: 0,
        };
        this.chunks[index] = Math.ceil(file.size / this.chunkSize);
        this.uploadedParts[index] = [];
        this.finishedParts[index] = 0;

        this.progress[index].totalSize = this.round(file.size / 1024);

        apiCalls.push(this.initializeUpload(file, index));
      });

      const results = await Promise.all(apiCalls);
      this.uploadId = results.map((item) => item.uploadId);

      resolve(true);
    });
  }

  // POST /multipart/init  →  { uploadId, key }
  async initializeUpload(file, index) {
    const key = `${this.folder}/${file.name}`;
    try {
      const response = await Api.post("/multipart/init", { key });
      const { uploadId } = response.data;

      this.uploadIdWithFiles[index] = uploadId;

      return { uploadId, fileKey: key };
    } catch (err) {
      throw new Error(err);
    }
  }

  // GET /multipart/part-url?key=&uploadId=&partNumber=  →  presigned URL string
  async getPartsUploadUrl(body, partNumber, file, index) {
    const key = `${this.folder}/${file.name}`;
    const uploadId = this.uploadId[index];

    try {
      const response = await Api.get("/multipart/part-url", {
        params: { key, uploadId, partNumber },
      });
      return response.data.url;
    } catch (err) {
      throw new Error(err);
    }
  }

  // POST /multipart/abort  →  { success }
  async abort() {
    Array.from(this.file).forEach(async (file, index) => {
      this.cancelTokenSource.cancel();
      const key = `${this.folder}/${file.name}`;

      await Api.post("/multipart/abort", {
        key,
        uploadId: this.uploadId[index],
      });
    });
  }

  async chunkUpload(url, partNumber, body, index) {
    axios
      .request({
        method: "PUT",
        url,
        data: body,
        headers: {
          "Content-Type": body.type,
        },
        cancelToken: this.cancelTokenSource.token,
        onUploadProgress: (progress) =>
          this.onProgressHandler(progress, partNumber, index),
      })
      .then(async (response) => {
        this.finishedParts[index] += 1;
        this.uploadedParts[index].push({
          ETag: response.headers.etag.replaceAll('"', ""),
          PartNumber: partNumber,
        });

        if (this.finishedParts[index] === this.chunks[index]) {
          setTimeout(() => {
            this.finalize(index);
          }, 500);
        }
      })
      .catch((err) => {
        const isNetworkError = err.message === "Network Error";

        if (isNetworkError) {
          this.failedChunks.push(
            this.chunkUpload(url, partNumber, body, index)
          );
        }
      });
  }

  async resizeFile(file) {
    return new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        110,
        110,
        "JPEG",
        100,
        0,
        (uri) => {
          resolve(uri);
        },
        "base64"
      );
    });
  }

  // POST /multipart/complete  →  { success, location }
  async finalize(index) {
    try {
      const key = `${this.folder}/${this.file[index].name}`;
      const parts = orderBy(this.uploadedParts[index], ["PartNumber"]);

      await Api.post("/multipart/complete", {
        key,
        uploadId: this.uploadId[index],
        parts,
      });

      this.uploadedFiles += 1;

      if (this.uploadedFiles === this.file.length) {
        const files = Array.from(this.file).map((item, i) => ({
          folder: this.folder,
          name: item.name,
          size: item.size,
          mime_type: item.type,
          lastModified: item.lastModified,
          uploadId: this.uploadIdWithFiles[i],
        }));

        const response = await Api.post("/finalize/" + this.folder, files);

        for (let i = 0; i < Array.from(this.file).length; i++) {
          if (!this.file[i]["type"].includes("image/")) {
            continue;
          }

          const thumb = await this.resizeFile(this.file[i]);

          await Api.post(`/save-thumb/${this.folder}`, {
            file: thumb,
            name: this.file[i]["name"],
          });
        }

        if (response.data.success) {
          this.setIsFinalized(true);
        }
      }
    } catch (err) {
      this.failedFinalzies.push(index);

      if (this.failedFinalzies.length) {
        this.failedFinalzies.forEach((item) => {
          this.finalize(item);
        });
      }

      throw new Error(err);
    }
  }
}
